import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

import { Biological } from '@/core/models/consultation.model'
import { labelToTranslationKey } from '@/pages/diagnostic/components/Mesures'
import { DictaphoneDocument } from '@/core/models/document.model'
import { Document as DocumentModel } from '@/core/models/document.model'
import { FormSubmission } from '@/core/models/form.model'
import FormSubmissionsList from '@/components/forms/FormSubmissionsList'
import { HistoricalConsultationDocuments } from '@/shared/documents'
import FilePreview from '@/components/ui/preview/FilePreview'
import { getFileExtension, getFilename } from '@/utils/document'

export type ConsultationDetailsProps = {
  consultation: {
    id: string
    pds: {
      fullName: string
      gender?: string
    }
    doctor: {
      fullName: string
      gender?: string
    }
    startDate: string
    fileCount: number
    reason?: string
    speciality?: string
    status: string
    mesures: Array<{ label: string; value: string | number | null; unit: string; order: number }>
    biologicals: Array<Biological>
    commentaire?: string
    dictaphones: DictaphoneDocument[]
    files: DocumentModel[]
    formSubmissions?: FormSubmission[]
  }
}

export function ConsultationDetails({ consultation }: ConsultationDetailsProps) {
  const [activeTab, setActiveTab] = useState(0)
  const { t } = useTranslation()

  return (
    <Box>
      <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} scrollButtons="auto" variant="scrollable">
        <Tab label={t('page.liveConsultation.consultationInfo.title')} />
        <Tab label={t('page.localConsultation.sidebar.mesures.label')} />
        <Tab label={t('page.localConsultation.sidebar.biologicals.label')} />
        <Tab label={t('page.localConsultation.sidebar.documents.label')} />
        {consultation.formSubmissions && <Tab label={t('page.localConsultation.tab.forms.title')} />}
      </Tabs>

      {activeTab === 0 && <ConsultationInfoTabContent consultation={consultation} />}

      {activeTab === 1 && <MeasuresTabContent consultation={consultation} />}

      {activeTab === 2 && <BiologicalsTabContent consultation={consultation} />}

      {activeTab === 3 && <DocumentsTabContent consultation={consultation} />}

      {activeTab === 4 && <FormsTabContent consultation={consultation} />}
    </Box>
  )
}

function BiologicalsTabContent({ consultation }: { consultation: ConsultationDetailsProps['consultation'] }) {
  const { t } = useTranslation()
  return (
    <Box p={2}>
      {consultation.biologicals.length > 0 ? (
        <Table>
          <TableBody>
            {consultation.biologicals.map((biological) => (
              <TableRow key={biological.id}>
                <TableCell>
                  <Typography variant="subtitle2">{biological.label}</Typography>
                  {biological.comment && (
                    <Typography variant="caption" color="textSecondary">
                      {biological.comment}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {biological.value} {biological.unit}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography>{t('page.localConsultation.tab.noBiologicals')}</Typography>
      )}
    </Box>
  )
}

function ConsultationInfoTabContent({ consultation }: { consultation: ConsultationDetailsProps['consultation'] }) {
  const { t } = useTranslation()
  return (
    <Box p={2}>
      <Typography variant="subtitle2" gutterBottom>
        {t('field.reason.label', { context: 'consultation' })}: {consultation.reason}
      </Typography>
      <Typography variant="subtitle2" gutterBottom>
        {t('field.speciality.label')}: {consultation.speciality}
      </Typography>
      <Typography variant="h6" gutterBottom>
        {t('field.comment.label')}
      </Typography>
      <Typography>{consultation.commentaire || '-'}</Typography>
    </Box>
  )
}

function DocumentsTabContent({ consultation }: { consultation: ConsultationDetailsProps['consultation'] }) {
  const containerRef = useRef<HTMLDivElement>(null)

  const documents = useMemo(() => {
    return consultation.files.map((file) => ({
      key: file.key,
      name: getFilename(file.filename),
      type: getFileExtension(file.filename),
      src: file.url,
    }))
  }, [consultation.files])

  const [selectedFile, setSelectedFile] = useState<typeof documents[number]>()

  return (
    <Box ref={containerRef} p={2} position="relative">
      <HistoricalConsultationDocuments documents={documents} onSelectFile={setSelectedFile} />
      <Dialog
        container={containerRef.current}
        fullScreen
        hideBackdrop
        onClose={() => setSelectedFile(undefined)}
        open={!!selectedFile}
        sx={{ position: 'absolute' }}
      >
        <DialogActions>
          <IconButton onClick={() => setSelectedFile(undefined)} color="inherit">
            <CloseRoundedIcon />
          </IconButton>
        </DialogActions>
        <DialogContent>
          {!!selectedFile && (
            <FilePreview PhotoViewerProps={{ disableAnnotation: true }} fileName={selectedFile.name} fileType={selectedFile.type} filePath={selectedFile.src} />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  )
}

function FormsTabContent({ consultation }: { consultation: ConsultationDetailsProps['consultation'] }) {
  const { t } = useTranslation()
  if (!consultation.formSubmissions) return null
  return (
    <Box p={2}>
      {consultation.formSubmissions.length > 0 ? (
        <FormSubmissionsList
          submissions={consultation.formSubmissions}
          isLoading={false}
          totalCount={consultation.formSubmissions.length}
          page={0}
          pageSize={10}
          onPageChange={(page) => {}}
          onPageSizeChange={(size) => {}}
        />
      ) : (
        <Typography>{t('page.localConsultation.tab.noSubmissions')}</Typography>
      )}
    </Box>
  )
}

function MeasuresTabContent({ consultation }: { consultation: ConsultationDetailsProps['consultation'] }) {
  const { t } = useTranslation()
  return (
    <Box p={2}>
      {consultation.mesures.length > 0 ? (
        <Table>
          <TableBody>
            {consultation.mesures.map((mesure) => (
              <TableRow key={mesure.label}>
                {/* mesure.label is the raw French label stored with the measure;
                    map it through the catalog so it follows the active locale. */}
                <TableCell>{t(labelToTranslationKey(mesure.label))}</TableCell>
                <TableCell>
                  {mesure.value} {mesure.unit}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography>{t('page.localConsultation.tab.noVitalSigns')}</Typography>
      )}
    </Box>
  )
}
